import * as React from 'react'

import { autorun } from 'mobx'
import { observer } from 'mobx-react'

import Localization from '~/client/src/shared/localization/LocalizationManager'

import type PhotoMarkupStore from './PhotoMarkup.store'
import type { IPointerInput } from './PhotoMarkupGestures'
import { drawImageLayer, drawScene } from './photoMarkupRender'
import PhotoMarkupTextEditor from './PhotoMarkupTextEditor'
import PhotoMarkupToolbars from './PhotoMarkupToolbars'
import usePhotoMarkupZoom from './usePhotoMarkupZoom'

interface IProps {
  store: PhotoMarkupStore
  stageRef: React.RefObject<HTMLDivElement>
}

function toPointerInput(event: React.PointerEvent<HTMLElement>): IPointerInput {
  const rect = event.currentTarget.getBoundingClientRect()
  return {
    point: { x: event.clientX - rect.left, y: event.clientY - rect.top },
    shiftKey: event.shiftKey,
    timeStamp: event.timeStamp,
  }
}

function PhotoMarkupStage({ store, stageRef }: IProps): JSX.Element {
  const imageCanvasRef = React.useRef<HTMLCanvasElement>(null)
  const markupCanvasRef = React.useRef<HTMLCanvasElement>(null)
  const pointerIdRef = React.useRef<number | null>(null)
  const { gestures } = store
  const zoom = usePhotoMarkupZoom(store, markupCanvasRef)

  React.useEffect(() => {
    const stage = stageRef.current
    const observer = new ResizeObserver(([entry]) =>
      store.setStageSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      }),
    )
    observer.observe(stage)
    return () => observer.disconnect()
  }, [store, stageRef])

  React.useEffect(
    () =>
      autorun(() => {
        const canvas = imageCanvasRef.current
        if (!canvas) return
        drawImageLayer(
          canvas,
          store.stageSize,
          window.devicePixelRatio || 1,
          store.image,
          store.imageSize,
          store.view,
        )
      }),
    [store],
  )

  React.useEffect(
    () =>
      autorun(() => {
        const canvas = markupCanvasRef.current
        if (!canvas) return
        drawScene(
          canvas,
          store.stageSize,
          window.devicePixelRatio || 1,
          store.scene,
        )
      }),
    [store],
  )

  const releasePointer = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const isOwner = event.pointerId === pointerIdRef.current
    if (isOwner) pointerIdRef.current = null
    return isOwner
  }

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (zoom.pointerDown(event)) return
    if (!event.isPrimary || event.button > 0) return
    if (pointerIdRef.current != null) return
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    pointerIdRef.current = event.pointerId
    gestures.pointerDown(toPointerInput(event))
  }

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (zoom.pointerMove(event)) return
    const owner = pointerIdRef.current
    if (owner == null ? event.isPrimary : event.pointerId === owner) {
      gestures.pointerMove(toPointerInput(event))
    }
  }

  const onPointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const isOwner = releasePointer(event)
    if (zoom.pointerEnd(event)) return
    if (isOwner) gestures.pointerUp(toPointerInput(event))
  }

  const onPointerCancel = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const isOwner = releasePointer(event)
    if (!zoom.pointerEnd(event) && isOwner) gestures.pointerCancel()
  }

  return (
    <div
      ref={stageRef}
      tabIndex={-1}
      className="tw:relative tw:shrink-0 tw:overflow-hidden tw:bg-muted tw:outline-none"
      style={
        store.stageFrame && {
          minWidth: store.stageFrame.width,
          height: store.stageFrame.height,
        }
      }
    >
      <canvas
        ref={imageCanvasRef}
        className="tw:absolute tw:inset-0 tw:size-full"
      />
      <canvas
        ref={markupCanvasRef}
        aria-label={Localization.translator.markUpPhoto}
        className="tw:absolute tw:inset-0 tw:size-full tw:touch-none"
        style={{ cursor: gestures.cursor }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onPointerLeave={gestures.pointerLeave}
        onContextMenu={event => event.preventDefault()}
      />
      {store.hasLoadFailed && (
        <div className="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:p-4 tw:text-muted-foreground">
          {Localization.translator.photoMarkupLoadFailed}
        </div>
      )}
      {store.image && (
        <>
          <PhotoMarkupTextEditor store={store} />
          <PhotoMarkupToolbars store={store} />
        </>
      )}
    </div>
  )
}

export default observer(PhotoMarkupStage)
